"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
const uuid_1 = require("uuid");
const constants_1 = require("./constants");
function sortTargets(targets) {
    let colorOrder = {
        green: 0,
        amber: 1,
        red: 2,
    };
    return targets.sort((a, b) => {
        if (a.wellNumber === b.wellNumber) {
            return colorOrder[a.emissionColor] - colorOrder[b.emissionColor];
        }
        else {
            return a.wellNumber - b.wellNumber;
        }
    });
}
exports.sortTargets = sortTargets;
exports.generateTimeString = (timestamp) => {
    const date = new Date(timestamp);
    const year = date.getFullYear();
    let month = date.getMonth() + 1;
    let day = date.getDate();
    const hour = date.getHours();
    const fixedHour = hour <= 12 ? hour : hour - 12;
    let minutes = date.getMinutes();
    day = day < 10 ? `0${day}` : day;
    month = month < 10 ? `0${month}` : month;
    minutes = minutes < 10 ? `0${minutes}` : minutes;
    return `${year}/${month}/${day} ${fixedHour}:${minutes} ${hour < 12 ? "AM" : "PM"}`;
};
/**
 * Function to calculate the corrected size of objects from the comps
 *
 * Necessary to use as our comps are not done proportionally, so different phone screens will react differently to fixed width values
 *
 * @param objectSize total width or height of the object in the comp
 * @param compSize total width or height of the comp
 * @param metricsValue the actual width or height of the phone
 */
function convertCompSize(objectSize, compSize, metricsValue) {
    let value = Math.floor((objectSize / compSize) * metricsValue);
    return value;
}
exports.convertCompSize = convertCompSize;
// // // // // // // // // // // // // // // // // // // // // // // //
// Protocol Creation Helpers
// // // // // // // // // // // // // // // // // // // // // // // //
const maxTempWarning = (temp) => `Maximum temperature is ${temp || constants_1.protocolCreationConstants.MAX_TEMPERATURE}`;
const minTempWarning = (temp) => `Minimum temperature is ${temp || constants_1.protocolCreationConstants.MIN_TEMPERATURE}`;
const maxDurationWarning = (time) => `Maximum duration is ${time ||
    constants_1.protocolCreationConstants.MAX_DURATION} seconds`;
const minDurationWarning = (time) => `Minimum duration is ${time ||
    constants_1.protocolCreationConstants.MIN_DURATION_ISO} seconds`;
const digitsOnly = (input) => {
    if (typeof input === "number") {
        return true;
    }
    const isDigit = [...input.toString()].every((c) => "0123456789".includes(c));
    return isDigit;
};
const computeColorMask = (greenActive, amberActive, redActive) => {
    const GREEN_MASK = +greenActive << 0;
    const AMBER_MASK = +amberActive << 1;
    const RED_MASK = +redActive << 2;
    const FINAL_MASK = GREEN_MASK | AMBER_MASK | RED_MASK;
    return FINAL_MASK;
};
const samplesPerMinuteToInterval = (count) => {
    switch (count) {
        case "2":
        case 2:
            return 30 * 1000;
        case "3":
        case 3:
            return 20 * 1000;
        case "4":
        case 4:
            return 15 * 1000;
        case 4:
            return 15 * 1000;
        default:
            return 30 * 1000;
    }
};
const checkAndSetProtocolVersionResolution = (protocol) => {
    let updatedProtocol = { ...protocol };
    const { version } = protocol;
    // Check to see if valid protocol version exists. If not, set it to current protocol version
    // This is relevant for the time period in which the backend has not been updated to return protocol version
    // or the default version has not been updated from "1" to "v2"
    if (!version || version === "1") {
        updatedProtocol.version = constants_1.protocolCreationConstants.PROTOCOL_VERSION;
    }
    // check to see if protocol version is supported in this app version; if not, block protocol from being displayed downstream
    const { version: updatedVersion } = updatedProtocol;
    if (constants_1.protocolCreationConstants.SUPPORTED_PROTOCOL_VERSIONS.includes(updatedVersion)) {
        updatedProtocol.toResolve = "nothing";
    }
    else {
        updatedProtocol.toResolve = "block";
    }
    return updatedProtocol;
};
const insertExtensionOvershoot = (protocol) => {
    const { extensionOvershootTemp, extensionOvershootTime, extensionTemp, extensionTime, } = protocol;
    let updatedProtocol = { ...protocol };
    if (extensionTemp &&
        (extensionOvershootTemp === null || extensionOvershootTemp === undefined)) {
        console.log(`adding extensionOvershootTemp of ${extensionTemp +
            constants_1.protocolCreationConstants.EXTENSION_OVERSHOOT_OFFSET}`);
        updatedProtocol.extensionOvershootTemp =
            extensionTemp + constants_1.protocolCreationConstants.EXTENSION_OVERSHOOT_OFFSET;
    }
    if (!extensionTemp) {
        console.log(`adding extensionOvershootTemp of 0`);
        updatedProtocol.extensionOvershootTemp = 0;
    }
    if (extensionTime &&
        (extensionOvershootTime === null || extensionOvershootTime === undefined)) {
        console.log(`adding extensionOvershootTime of ${constants_1.protocolCreationConstants.EXTENSION_OVERSHOOT_DURATION}`);
        updatedProtocol.extensionOvershootTime =
            constants_1.protocolCreationConstants.EXTENSION_OVERSHOOT_DURATION;
    }
    if (!extensionTime) {
        console.log("adding extensionOvershootTime of 0");
        updatedProtocol.extensionOvershootTime = 0;
    }
    return updatedProtocol;
};
const validateProtocol = (values) => {
    let errors = {};
    const NAME_ERROR = "Name cannot exceed 30 characters";
    if (!values.name) {
        errors.name = "Name is required";
    }
    else if (values.name.length > 30) {
        errors.name = NAME_ERROR;
    }
    if (values.greenActive && values.greenTargetName.length > 30) {
        errors.greenTargetName = NAME_ERROR;
    }
    if (values.amberActive && values.amberTargetName.length > 30) {
        errors.amberTargetName = NAME_ERROR;
    }
    if (values.redActive && values.redTargetName.length > 30) {
        errors.redTargetName = NAME_ERROR;
    }
    /* First denature */
    /* Temp */
    if (!digitsOnly(values.firstDenatureTemp)) {
        errors.firstDenatureTemp =
            constants_1.protocolCreationConstants.ALLOWED_TEMP_CHARACTERS_WARNING;
    }
    else if (values.firstDenatureTemp > constants_1.protocolCreationConstants.MAX_TEMPERATURE) {
        errors.firstDenatureTemp = maxTempWarning();
    }
    else if (values.firstDenatureTemp < constants_1.protocolCreationConstants.MIN_TEMPERATURE) {
        errors.firstDenatureTemp = minTempWarning();
    }
    /* Time */
    if (!digitsOnly(values.firstDenatureTime)) {
        errors.firstDenatureTime =
            constants_1.protocolCreationConstants.ALLOWED_DURATION_CHARACTERS_WARNING;
    }
    else if (values.firstDenatureTime > constants_1.protocolCreationConstants.MAX_DURATION) {
        errors.firstDenatureTime = maxDurationWarning();
    }
    /* Cycling denature */
    /* Temp */
    if (!digitsOnly(values.denatureTemp)) {
        errors.denatureTemp =
            constants_1.protocolCreationConstants.ALLOWED_TEMP_CHARACTERS_WARNING;
    }
    else if (values.denatureTemp > constants_1.protocolCreationConstants.MAX_TEMPERATURE) {
        errors.denatureTemp = maxTempWarning();
    }
    else if (values.denatureTemp < constants_1.protocolCreationConstants.MIN_TEMPERATURE) {
        errors.denatureTemp = minTempWarning();
    }
    /* Time */
    if (!values.denatureTime) {
        errors.denatureTime = "You must supply a denature time";
    }
    else if (!digitsOnly(values.denatureTime)) {
        errors.denatureTime =
            constants_1.protocolCreationConstants.ALLOWED_DURATION_CHARACTERS_WARNING;
    }
    else if (values.denatureTime < 1) {
        errors.denatureTime = "Minimum denature time is 1 second";
    }
    else if (values.denatureTime > constants_1.protocolCreationConstants.MAX_DURATION) {
        errors.denatureTime = maxDurationWarning();
    }
    /* Cycling Anneal */
    /* Temperature */
    if (!values.annealTemp) {
        errors.annealTemp = "You must supply an anneal temperature";
    }
    else if (parseFloat(values.annealTemp) !== 0 &&
        !parseFloat(values.annealTemp)) {
        errors.annealTemp =
            constants_1.protocolCreationConstants.ALLOWED_TEMP_CHARACTERS_WARNING;
    }
    else if (values.annealTemp > constants_1.protocolCreationConstants.MAX_TEMPERATURE) {
        errors.annealTemp = maxTempWarning();
    }
    else if (values.annealTemp < constants_1.protocolCreationConstants.MIN_TEMPERATURE) {
        errors.annealTemp = minTempWarning();
    }
    /* Time */
    if (!values.annealTime) {
        errors.annealTime = "You must supply an anneal time";
    }
    else if (!digitsOnly(values.annealTime)) {
        errors.annealTime =
            constants_1.protocolCreationConstants.ALLOWED_DURATION_CHARACTERS_WARNING;
    }
    else if (values.annealTime > constants_1.protocolCreationConstants.MAX_DURATION) {
        errors.annealTime = maxDurationWarning();
    }
    else if (!values.showExtension &&
        values.annealTime < constants_1.protocolCreationConstants.MIN_ANNEAL_TIME) {
        errors.annealTime = "Minimum extension time is 9 seconds";
    }
    /* Extension */
    /* Temperature */
    if (values.showExtension && !digitsOnly(values.extensionTemp)) {
        errors.extensionTemp =
            constants_1.protocolCreationConstants.ALLOWED_TEMP_CHARACTERS_WARNING;
    }
    else if (values.showExtension &&
        values.extensionTemp > constants_1.protocolCreationConstants.MAX_TEMPERATURE) {
        errors.extensionTemp = maxTempWarning();
    }
    else if (values.showExtension &&
        values.extensionTemp < constants_1.protocolCreationConstants.MIN_TEMPERATURE) {
        errors.extensionTemp = minTempWarning();
    }
    /* Time */
    if (values.showExtension && !digitsOnly(values.extensionTime)) {
        errors.extensionTime =
            constants_1.protocolCreationConstants.ALLOWED_DURATION_CHARACTERS_WARNING;
    }
    else if (values.showExtension &&
        values.extensionTime < constants_1.protocolCreationConstants.MIN_ANNEAL_TIME) {
        errors.extensionTime = "Extension time must be at least 9 seconds";
    }
    else if (values.showExtension &&
        values.extensionTime > constants_1.protocolCreationConstants.MAX_DURATION) {
        errors.extensionTime = maxDurationWarning();
    }
    /* Isothermal */
    /* Temperature */
    if (!values.showHeatingMelt) {
        if (values.showIsothermal && !digitsOnly(values.isothermalTemp)) {
            errors.isothermalTemp =
                constants_1.protocolCreationConstants.ALLOWED_TEMP_CHARACTERS_WARNING;
        }
        else if (values.showIsothermal &&
            values.isothermalTemp > constants_1.protocolCreationConstants.MAX_TEMPERATURE) {
            errors.isothermalTemp = maxTempWarning();
        }
        else if (values.showIsothermal &&
            values.isothermalTemp < constants_1.protocolCreationConstants.MIN_TEMPERATURE_ISO) {
            errors.isothermalTemp = minTempWarning(constants_1.protocolCreationConstants.MIN_TEMPERATURE_ISO);
        }
        else if (values.showIsothermal &&
            values.showRT &&
            values.isothermalTemp < values.rtTemp) {
            errors.isothermalTemp =
                "Isothermal temperature must be greater than RT temperature";
        }
    }
    /* Time */
    if (!values.showHeatingMelt) {
        if (values.showIsothermal && !digitsOnly(values.isothermalTotalTime)) {
            errors.isothermalTotalTime =
                constants_1.protocolCreationConstants.ALLOWED_DURATION_CHARACTERS_WARNING;
        }
        else if (values.showIsothermal &&
            values.isothermalTotalTime > constants_1.protocolCreationConstants.MAX_DURATION_ISO) {
            errors.isothermalTotalTime = "Maximum duration is 60 mins";
        }
        else if (values.showIsothermal &&
            values.isothermalTotalTime < constants_1.protocolCreationConstants.MIN_DURATION_ISO) {
            errors.isothermalTotalTime = "Minimum duration is 10 mins";
        }
    }
    /* RT */
    if (!values.showHeatingMelt) {
        /* Temperature */
        if (values.showRT && !digitsOnly(values.rtTemp)) {
            errors.rtTemp = constants_1.protocolCreationConstants.ALLOWED_TEMP_CHARACTERS_WARNING;
        }
        else if (values.showRT &&
            values.rtTemp > constants_1.protocolCreationConstants.MAX_TEMPERATURE) {
            errors.rtTemp = maxTempWarning();
        }
        else if (values.showRT &&
            values.rtTemp < constants_1.protocolCreationConstants.MIN_TEMPERATURE_RT) {
            errors.rtTemp = minTempWarning(constants_1.protocolCreationConstants.MIN_TEMPERATURE_RT);
        }
        /* Time */
        if (values.showRT && !digitsOnly(values.rtTime)) {
            errors.rtTime =
                constants_1.protocolCreationConstants.ALLOWED_DURATION_CHARACTERS_WARNING;
        }
        else if (values.showRT && values.rtTime < 1) {
            errors.rtTime = "You must supply an RT time";
        }
        else if (values.showRT &&
            values.rtTime > constants_1.protocolCreationConstants.MAX_DURATION) {
            errors.rtTime = maxDurationWarning();
        }
    }
    /* Channels */
    if (!values.greenActive && !values.redActive && !values.amberActive) {
        errors.greenActive = "At least one channel must be active";
    }
    /* Melt */
    /* Temperature */
    if ((values.showHeatingMelt || values.showCoolingMelt) &&
        values.meltTempStart % 1 !== 0) {
        errors.meltTempStart = "Temperature must be an integer";
    }
    if ((values.showHeatingMelt || values.showCoolingMelt) &&
        values.meltTempFinal % 1 !== 0) {
        errors.meltTempFinal = "Temperature must be an integer";
    }
    if ((values.showHeatingMelt || values.showCoolingMelt) &&
        !digitsOnly(values.meltTempStart)) {
        errors.meltTempStart =
            constants_1.protocolCreationConstants.ALLOWED_TEMP_CHARACTERS_WARNING;
    }
    else if ((values.showHeatingMelt || values.showCoolingMelt) &&
        values.meltTempStart > constants_1.protocolCreationConstants.MAX_TEMPERATURE_MELT) {
        errors.meltTempStart = maxTempWarning(constants_1.protocolCreationConstants.MAX_TEMPERATURE_MELT);
    }
    else if (values.showHeatingMelt &&
        values.meltTempStart < constants_1.protocolCreationConstants.MIN_TEMPERATURE_MELT) {
        errors.meltTempStart = minTempWarning(constants_1.protocolCreationConstants.MIN_TEMPERATURE_MELT);
    }
    if (values.showHeatingMelt &&
        values.meltTempFinal > constants_1.protocolCreationConstants.MAX_TEMPERATURE_MELT) {
        errors.meltTempFinal = maxTempWarning(constants_1.protocolCreationConstants.MAX_TEMPERATURE_MELT);
    }
    else if (values.showHeatingMelt &&
        values.meltTempStart >= values.meltTempFinal) {
        errors.meltTempFinal =
            "Final temperature must be greater than start temperature";
    }
    if (values.showCoolingMelt &&
        values.meltTempFinal < constants_1.protocolCreationConstants.MIN_TEMPERATURE_MELT) {
        errors.meltTempFinal = minTempWarning(constants_1.protocolCreationConstants.MIN_TEMPERATURE_MELT);
    }
    else if (values.showCoolingMelt &&
        values.meltTempStart <= values.meltTempFinal) {
        errors.meltTempFinal =
            "Final temperature must be less than start temperature";
    }
    /* Cycles*/
    if (values.cycles > 240) {
        errors.cycles = "Max cycle count is 240.";
    }
    else if (values.cycles < 1) {
        errors.cycles = "Minimum cycle count is 1.";
    }
    return errors;
};
const formatValuesToProtocol = (values) => {
    const { firstDenatureTemp, firstDenatureTime, denatureTemp, denatureTime, annealTemp, annealTime, rtTemp, rtTime, extensionTemp, extensionTime, name, greenTargetName, amberTargetName, redTargetName, cycles, greenActive, amberActive, redActive, } = values;
    let protocolInfo = {
        name,
        greenTargetName,
        amberTargetName,
        redTargetName,
        firstDenatureTemp: parseInt(firstDenatureTemp),
        firstDenatureTime: parseInt(firstDenatureTime),
        denatureTemp: parseInt(denatureTemp),
        denatureTime: parseInt(denatureTime),
        annealTemp: parseFloat(annealTemp),
        annealTime: parseInt(annealTime),
        rtTemp: parseInt(rtTemp),
        rtTime: parseInt(rtTime),
        extensionTemp: parseInt(extensionTemp),
        extensionTime: parseInt(extensionTime),
        cycles: parseInt(cycles),
        colorMask: computeColorMask(greenActive, amberActive, redActive),
        version: constants_1.protocolCreationConstants.CUSTOM_PROTOCOL_VERSION,
        toResolve: "nothing",
    };
    // extensionOvershoot properties are stored in their own object to
    // This is to allow the data to go from PreformattedProtocolInfo to FormattedProtocolInfo types
    let extensionOvershoot = {
        extensionOvershootTime: 0,
        extensionOvershootTemp: 0,
    };
    // Remove rt values if not selected
    if (!values.showRT) {
        protocolInfo = {
            ...protocolInfo,
            rtTime: 0,
            rtTemp: 0,
        };
    }
    // Remove extension values if not selected
    if (!values.showExtension) {
        protocolInfo = {
            ...protocolInfo,
            extensionTemp: 0,
            extensionTime: 0,
        };
        extensionOvershoot = {
            ...extensionOvershoot,
            extensionOvershootTime: 0,
            extensionOvershootTemp: 0,
        };
    }
    else {
        extensionOvershoot = {
            ...extensionOvershoot,
            extensionOvershootTime: constants_1.protocolCreationConstants.EXTENSION_OVERSHOOT_DURATION / 1000,
            extensionOvershootTemp: protocolInfo.extensionTemp +
                constants_1.protocolCreationConstants.EXTENSION_OVERSHOOT_OFFSET,
        };
    }
    if (values.showIsothermal) {
        const { isothermalTemp, isothermalSampleFrequency, isothermalTotalTime, } = values;
        const isothermalCycles = parseInt(isothermalSampleFrequency) * parseInt(isothermalTotalTime);
        const sampleInterval = samplesPerMinuteToInterval(isothermalSampleFrequency);
        protocolInfo = {
            ...protocolInfo,
            isothermalTemp: parseFloat(isothermalTemp),
            isothermalInterval: sampleInterval,
            cycles: isothermalCycles,
            annealTime: 0,
            annealTemp: 0,
            denatureTime: 0,
            denatureTemp: 0,
        };
    }
    // Melt protocol takes priority over isothermal or RT being toggled on
    if (values.showHeatingMelt || values.showCoolingMelt) {
        let { meltTempStart, meltTempFinal, meltIncrement } = values;
        meltIncrement = parseFloat(meltIncrement);
        meltTempStart = parseInt(meltTempStart);
        meltTempFinal = parseInt(meltTempFinal);
        const steps = (meltTempFinal - meltTempStart) / meltIncrement + 1;
        protocolInfo = {
            ...protocolInfo,
            meltIncrement,
            meltDuration: constants_1.protocolCreationConstants.DEFAULT_MELT_DURATION,
            meltTempStart,
            meltTempFinal,
            cycles: steps,
            annealTime: 0,
            annealTemp: 0,
            denatureTime: 0,
            denatureTemp: 0,
            firstDenatureTime: 0,
            firstDenatureTemp: 0,
            rtTemp: 0,
            rtTime: 0,
        };
        delete protocolInfo.isothermalTemp;
        delete protocolInfo.isothermalInterval;
    }
    let formattedProtocol = {
        ...protocolInfo,
        ...extensionOvershoot,
    };
    return formattedProtocol;
};
exports.generateUuid = () => {
    const generatedUuid = uuid_1.v4();
    const formattedUuid = generatedUuid.toLowerCase();
    return formattedUuid;
};
exports.uuidHelpers = {
    generate: exports.generateUuid,
};
exports.protocolCreationHelpers = {
    digitsOnly,
    computeColorMask,
    samplesPerMinuteToInterval,
    checkAndSetProtocolVersionResolution,
    insertExtensionOvershoot,
    validateProtocol,
    formatValuesToProtocol,
    maxTempWarning,
    minTempWarning,
    maxDurationWarning,
    minDurationWarning,
};
